<!-- src/views/ForbiddenAccess.vue -->
<template>
  <div class="forbidden-page">
    <!-- Animated background shapes -->
    <div class="bg-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
    </div>

    <div class="forbidden-container">
      <!-- Header com animação -->
      <div class="forbidden-header">
        <div class="lock-icon">
          <div class="lock-body">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"
              />
            </svg>
          </div>
        </div>

        <div class="error-code">
          <span class="code-number">4</span>
          <span class="code-number pulse">0</span>
          <span class="code-number">3</span>
        </div>

        <h1 class="error-title">Acesso Restrito</h1>
        <p class="error-subtitle">
          Você não tem permissão para acessar este recurso.
        </p>
      </div>

      <!-- Card de informações do usuário -->
      <div class="user-card">
        <div class="user-avatar">
          {{ userInitials }}
        </div>
        <div class="user-info">
          <span class="user-name" :title="userName">{{ userName }}</span>
          <span class="user-email" :title="userEmail">{{ userEmail }}</span>
        </div>
        <div class="user-role" :class="profileTypeClass">
          <i :class="profileTypeIcon" class="role-icon-fa"></i>
          <span class="role-name">{{ profileTypeLabel }}</span>
        </div>
      </div>

      <!-- Detalhes simplificados -->
      <div class="access-details">
        <div class="detail-row">
          <div class="detail-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          </div>
          <div class="detail-content">
            <span class="detail-label">Página solicitada</span>
            <code class="detail-value">{{ requestedPath }}</code>
          </div>
        </div>

        <div class="detail-row" v-if="requiredLevel !== null">
          <div class="detail-icon warning">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div class="detail-content">
            <span class="detail-label">Nível necessário</span>
            <span class="detail-value required">
              {{ requiredLevelName }}
              <span class="level-num">(Nível {{ requiredLevel }}+)</span>
            </span>
          </div>
        </div>

        <div
          class="permission-gap"
          v-if="requiredLevel !== null && requiredLevel > adminLevel"
        >
          <div class="gap-bar">
            <div
              class="gap-current"
              :style="{ width: levelProgress + '%' }"
            ></div>
            <div
              class="gap-required"
              :style="{ left: requiredProgress + '%' }"
            ></div>
          </div>
          <div class="gap-labels">
            <span>Seu nível: {{ adminLevel }}</span>
            <span>Necessário: {{ requiredLevel }}</span>
          </div>
        </div>
      </div>

      <!-- Ações principais -->
      <div class="actions-section">
        <button @click="goBack" class="action-btn back">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Voltar
        </button>

        <button @click="goHome" class="action-btn home">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Página Inicial
        </button>

        <button @click="handleLogout" class="action-btn logout">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Trocar Conta
        </button>
      </div>

      <!-- Seção de ajuda colapsável -->
      <details class="help-section">
        <summary class="help-toggle">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          Precisa de ajuda?
          <svg
            class="chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </summary>

        <div class="help-content">
          <div class="help-options">
            <div class="help-option">
              <div class="option-icon">�</div>
              <div class="option-info">
                <h4>Copiar Relatório</h4>
                <p>Copie os detalhes técnicos do erro</p>
                <button
                  @click="copyErrorReport"
                  class="option-link"
                  :class="{ success: reportCopied }"
                >
                  {{ reportCopied ? "✓ Copiado!" : "Copiar detalhes" }}
                  <svg
                    v-if="!reportCopied"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path
                      d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Preview do relatório -->
          <details class="report-preview">
            <summary>Ver relatório completo</summary>
            <pre>{{ errorReport }}</pre>
          </details>

          <div class="error-id-box">
            <span class="id-label">ID do Erro</span>
            <code class="id-value">{{ errorId }}</code>
          </div>
        </div>
      </details>

      <!-- Footer discreto -->
      <div class="footer-info">
        <span>{{ currentDateTime }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { ADMIN_LEVEL_NAMES } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();

const reportCopied = ref(false);

// Gera um ID único para o erro
const errorId = computed(() => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `ERR-${timestamp}-${random}`.toUpperCase();
});

// Informações do usuário
const profileType = computed(
  () => auth.profile?.profile_type || auth.profileType || "Unknown",
);
const adminLevel = computed(() => auth.adminLevelValue || 0);
const adminLevelName = computed(
  () =>
    ADMIN_LEVEL_NAMES[adminLevel.value] ||
    auth.adminLevelDisplayName ||
    "Desconhecido",
);
const userEmail = computed(() => auth.profile?.email || "Não identificado");
const userName = computed(() => {
  const profile = auth.profile;
  // Pega first_name e last_name diretamente
  if (profile?.first_name) {
    return `${profile.first_name} ${profile.last_name || ""}`.trim();
  }
  // Fallback para email
  return userEmail.value.split("@")[0];
});

const userInitials = computed(() => {
  const name = userName.value;
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

// Profile type info (role)
const profileTypeLabel = computed(() => {
  const type = profileType.value;
  const labels = {
    Admin: "Administrador",
    HCAdmin: "Admin de Unidade",
    Doctor: "Médico",
    Patient: "Paciente",
    Unknown: "Desconhecido",
  };
  return labels[type] || type;
});

const profileTypeClass = computed(() => {
  const type = profileType.value;
  return `role-${type.toLowerCase()}`;
});

const profileTypeIcon = computed(() => {
  const type = profileType.value;
  const icons = {
    Admin: "fas fa-user-shield",
    HCAdmin: "fas fa-hospital-user",
    Doctor: "fas fa-user-md",
    Patient: "fas fa-user",
    Unknown: "fas fa-question-circle",
  };
  return icons[type] || "fas fa-user";
});

// Informações da rota
const requestedPath = computed(() => {
  return (
    route.query.from ||
    route.query.url ||
    document.referrer?.split(window.location.origin)[1] ||
    route.fullPath
  );
});

// Nível requerido (se disponível)
const requiredLevel = computed(() => {
  const level = route.query.required;
  return level ? parseInt(level) : null;
});

const requiredLevelName = computed(() => {
  if (requiredLevel.value === null) return "";
  return (
    ADMIN_LEVEL_NAMES[requiredLevel.value] || `Nível ${requiredLevel.value}`
  );
});

// Progress bar calculation
const maxLevel = 5;
const levelProgress = computed(() =>
  Math.min((adminLevel.value / maxLevel) * 100, 100),
);
const requiredProgress = computed(() => {
  if (requiredLevel.value === null) return 0;
  return Math.min((requiredLevel.value / maxLevel) * 100, 100);
});

// Data/hora atual formatada
const currentDateTime = computed(() => {
  return new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});

// Relatório de erro para copiar/enviar
const errorReport = computed(() => {
  const profile = auth.profile || {};
  const isAdmin = profileType.value === "Admin";

  let report = `=== Relatório de Acesso Negado ===
ID: ${errorId.value}
Data: ${currentDateTime.value}

--- Perfil Logado ---
Nome: ${userName.value}
E-mail: ${userEmail.value}
Tipo de Perfil: ${profileTypeLabel.value} (${profileType.value})`;

  // Se for Admin, mostra o nível
  if (isAdmin) {
    report += `
Nível Admin: ${adminLevelName.value} (${adminLevel.value})`;
  }

  // Adiciona mais dados do perfil se disponíveis
  if (profile.profile_id) {
    report += `
Profile ID: ${profile.profile_id}`;
  }
  if (profile.health_center_id) {
    report += `
Health Center ID: ${profile.health_center_id}`;
  }

  report += `

--- Acesso Solicitado ---
Página: ${requestedPath.value}`;

  if (requiredLevel.value !== null) {
    report += `
Nível Necessário: ${requiredLevelName.value} (${requiredLevel.value}+)`;
  }

  report += `

--- Sistema ---
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
`;

  return report;
});

// Ações
function goBack() {
  if (window.history.length > 2) {
    router.back();
  } else {
    router.push("/home");
  }
}

function goHome() {
  router.push("/home");
}

function handleLogout() {
  auth.logout();
}

async function copyErrorReport() {
  try {
    await navigator.clipboard.writeText(errorReport.value);
    reportCopied.value = true;
    setTimeout(() => {
      reportCopied.value = false;
    }, 3000);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = errorReport.value;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    reportCopied.value = true;
    setTimeout(() => {
      reportCopied.value = false;
    }, 3000);
  }
}

onMounted(() => {
  logger.warn("Acesso Negado:", {
    errorId: errorId.value,
    requestedPath: requestedPath.value,
    userLevel: adminLevel.value,
    requiredLevel: requiredLevel.value,
  });
});
</script>

<style scoped>
/* =========================
   FORBIDDEN ACCESS PAGE
   Modern, friendly 403 page
   ========================= */

.forbidden-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

/* Animated background shapes */
.bg-shapes {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  animation: float 20s infinite ease-in-out;
}

.shape-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.shape-2 {
  width: 300px;
  height: 300px;
  bottom: -50px;
  right: -50px;
  animation-delay: -5s;
}

.shape-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  animation-delay: -10s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

/* Main container */
.forbidden-container {
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.25);
  max-width: 480px;
  width: 100%;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Header */
.forbidden-header {
  text-align: center;
  padding: 40px 32px 32px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.lock-icon {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.35);
}

.error-code {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.code-number {
  font-size: 48px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
}

.code-number.pulse {
  animation: pulse 2s infinite;
  color: #ef4444;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.error-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
}

.error-subtitle {
  font-size: 15px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* User card */
.user-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  margin: 20px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.user-email {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* User role badge */
.user-role {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
}

.role-icon-fa {
  font-size: 12px;
}

/* Role type colors */
.role-admin {
  background: #dbeafe;
  color: #1d4ed8;
}
.role-hcadmin {
  background: #dcfce7;
  color: #15803d;
}
.role-doctor {
  background: #fef3c7;
  color: #b45309;
}
.role-patient {
  background: #f3e8ff;
  color: #7c3aed;
}
.role-unknown {
  background: #f1f5f9;
  color: #64748b;
}

.level-icon-fa {
  font-size: 12px;
}

.level-disabled {
  background: #f1f5f9;
  color: #64748b;
}
.level-basic {
  background: #dbeafe;
  color: #1d4ed8;
}
.level-super {
  background: #fef3c7;
  color: #b45309;
}
.level-master {
  background: #ede9fe;
  color: #7c3aed;
}
.level-god {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
}

/* Access details */
.access-details {
  padding: 0 24px 20px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.detail-row:last-of-type {
  border-bottom: none;
}

.detail-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-icon.warning {
  background: #fef3c7;
  color: #b45309;
}

.detail-content {
  flex: 1;
  min-width: 0;
}

.detail-label {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
  word-break: break-all;
}

.detail-value.required {
  color: #dc2626;
  font-weight: 600;
}

.level-num {
  font-weight: 400;
  color: #64748b;
}

code.detail-value {
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: "SF Mono", "Monaco", monospace;
  font-size: 13px;
  display: inline-block;
  max-width: 100%;
}

/* Permission gap visualization */
.permission-gap {
  margin-top: 16px;
  padding: 16px;
  background: #fef2f2;
  border-radius: 12px;
  border: 1px solid #fecaca;
}

.gap-bar {
  height: 8px;
  background: #fee2e2;
  border-radius: 4px;
  position: relative;
  overflow: visible;
}

.gap-current {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.gap-required {
  position: absolute;
  top: -4px;
  width: 3px;
  height: 16px;
  background: #dc2626;
  border-radius: 2px;
  transform: translateX(-50%);
}

.gap-required::after {
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #dc2626;
}

.gap-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  color: #64748b;
}

/* Actions */
.actions-section {
  display: flex;
  gap: 10px;
  padding: 0 24px 20px;
}

.action-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: #f8fafc;
  color: #475569;
}

.action-btn:hover {
  transform: translateY(-2px);
}

.action-btn.back:hover {
  background: #f1f5f9;
  color: #334155;
}

.action-btn.home {
  background: #667eea;
  color: white;
}

.action-btn.home:hover {
  background: #5a67d8;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.action-btn.logout {
  background: #fef2f2;
  color: #dc2626;
}

.action-btn.logout:hover {
  background: #fee2e2;
}

/* Help section */
.help-section {
  margin: 0 24px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
}

.help-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  background: #f8fafc;
  user-select: none;
  list-style: none;
}

.help-toggle::-webkit-details-marker {
  display: none;
}

.help-toggle .chevron {
  margin-left: auto;
  transition: transform 0.2s;
}

.help-section[open] .help-toggle .chevron {
  transform: rotate(180deg);
}

.help-content {
  padding: 16px;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.help-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.help-option {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}

.option-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.option-info {
  flex: 1;
}

.option-info h4 {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.option-info p {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 8px;
  line-height: 1.4;
}

.option-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #667eea;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
}

.option-link:hover {
  color: #5a67d8;
}

.option-link.success {
  color: #059669;
}

/* Report preview */
.report-preview {
  margin-top: 12px;
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
}

.report-preview summary {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  user-select: none;
}

.report-preview summary:hover {
  background: #f1f5f9;
}

.report-preview pre {
  padding: 12px;
  margin: 0;
  font-size: 11px;
  font-family: "SF Mono", "Monaco", monospace;
  white-space: pre-wrap;
  word-break: break-word;
  background: #1e293b;
  color: #e2e8f0;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.5;
}

.error-id-box {
  margin-top: 16px;
  padding: 12px;
  background: #1e293b;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.id-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.id-value {
  font-size: 12px;
  font-family: "SF Mono", "Monaco", monospace;
  color: #f1f5f9;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

/* Footer */
.footer-info {
  text-align: center;
  padding: 12px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #94a3b8;
}

/* =========================
   RESPONSIVE - Mobile First
   ========================= */

@media (max-width: 520px) {
  .forbidden-page {
    padding: 16px;
    align-items: flex-start;
    padding-top: 40px;
  }

  .forbidden-container {
    border-radius: 20px;
  }

  .forbidden-header {
    padding: 32px 20px 24px;
  }

  .lock-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    margin-bottom: 16px;
  }

  .lock-icon svg {
    width: 28px;
    height: 28px;
  }

  .code-number {
    font-size: 40px;
  }

  .error-title {
    font-size: 20px;
  }

  .error-subtitle {
    font-size: 14px;
  }

  .user-card {
    margin: 16px;
    padding: 14px;
    gap: 12px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    font-size: 13px;
  }

  .user-level {
    padding: 5px 10px;
    font-size: 11px;
  }

  .access-details {
    padding: 0 16px 16px;
  }

  .detail-row {
    padding: 10px 0;
  }

  .detail-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }

  .detail-icon svg {
    width: 16px;
    height: 16px;
  }

  .detail-label {
    font-size: 11px;
  }

  .detail-value {
    font-size: 13px;
  }

  code.detail-value {
    font-size: 11px;
  }

  .permission-gap {
    padding: 12px;
  }

  .gap-labels {
    font-size: 10px;
  }

  .actions-section {
    padding: 0 16px 16px;
    gap: 8px;
  }

  .action-btn {
    padding: 14px 8px;
    font-size: 11px;
    border-radius: 12px;
  }

  .action-btn svg {
    width: 18px;
    height: 18px;
  }

  .help-section {
    margin: 0 16px 16px;
    border-radius: 12px;
  }

  .help-toggle {
    padding: 12px 14px;
    font-size: 13px;
  }

  .help-content {
    padding: 14px;
  }

  .help-option {
    padding: 10px;
    gap: 10px;
  }

  .option-icon {
    font-size: 20px;
  }

  .option-info h4 {
    font-size: 12px;
  }

  .option-info p {
    font-size: 11px;
  }

  .option-link {
    font-size: 11px;
  }

  .error-id-box {
    padding: 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .footer-info {
    padding: 10px 16px;
    font-size: 11px;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .forbidden-page {
    padding: 12px;
    padding-top: 24px;
  }

  .code-number {
    font-size: 32px;
  }

  .error-title {
    font-size: 18px;
  }

  .user-card {
    flex-wrap: wrap;
  }

  .user-level {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }

  .actions-section {
    flex-wrap: wrap;
  }

  .action-btn {
    min-width: calc(50% - 4px);
    flex: 1 1 calc(50% - 4px);
  }

  .action-btn.logout {
    flex: 1 1 100%;
  }
}

/* Landscape on small devices */
@media (max-height: 600px) and (orientation: landscape) {
  .forbidden-page {
    padding: 12px;
    align-items: flex-start;
  }

  .forbidden-container {
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }

  .forbidden-header {
    padding: 20px;
  }

  .lock-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }

  .code-number {
    font-size: 32px;
  }
}
</style>
